import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';
import { testsApi } from '@/api/tests';
import { Copy, Card, Eye, MoreVertical, ArrowRotateLeft, Share } from '@/components/icons';
import WhatsAppIcon from '@/components/icons/WhatsAppIcon';
import { SubscriptionPlansModal } from '@/components/modals/SubscriptionPlansModal';
import { DataTableCell, DataTableRow } from '@/components/system';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useModalContext } from '@/components/ui/modal-provider';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { cn } from '@/lib/utils';
import { formatDateTime } from './formatters';
import { shareTestLink } from './shareTestLink';
import { TestConsolidationBadge, TestStatusBadge } from './StatusBadges';
import type { PatientTestOverview } from '@/types/patient-overview';

type Props = {
    patientId: string;
    test: PatientTestOverview;
    onViewAnswers: (payload: { assignmentId: string; testName: string }) => void;
};

const isCompleted = (status: string) => ['completed', 'COMPLETED'].includes(status);

const getRetryError = (error: unknown) => {
    if (isAxiosError(error)) {
        const payload = error.response?.data as
            | { description?: string; reason?: string }
            | undefined;

        return {
            message: payload?.description || 'Não foi possível reenfileirar a consolidação.',
            reason: payload?.reason,
        };
    }

    if (error instanceof Error && error.message.trim()) {
        return {
            message: error.message,
            reason: undefined,
        };
    }

    return {
        message: 'Não foi possível reenfileirar a consolidação.',
        reason: undefined,
    };
};

export default function PatientTestsRow({ patientId, test, onViewAnswers }: Props) {
    const queryClient = useQueryClient();
    const { closeModal, openModal } = useModalContext();
    const isOverdue = test.assignment_state === 'overdue';
    const isCompletedAssignment = isCompleted(test.status);
    const correctWithAiAccess = useFeatureEntitlement('tests.correctWithAi');

    const retryMutation = useMutation({
        mutationFn: async () => testsApi.retryConsolidation(patientId, test.id),
        onSuccess: () => {
            toast.success('Consolidação automática reenfileirada');
        },
        onError: (error: unknown) => {
            const parsed = getRetryError(error);
            toast.error(parsed.message);
        },
        onSettled: async () => {
            // Prefix key: the detail page reads assignments from the patient
            // overview, other surfaces from the dedicated tests query.
            await queryClient.invalidateQueries({ queryKey: ['patients', patientId] });
        },
    });

    const handleShare = (action: 'copy' | 'whatsapp') =>
        shareTestLink({
            patientId,
            assignmentId: test.id,
            action,
        });

    const handleOpenPlansModal = () => {
        openModal({
            title: 'Conheça nossos planos',
            content: (
                <SubscriptionPlansModal
                    close={closeModal}
                    description="Seus créditos de IA acabaram. O paciente pode continuar respondendo instrumentos normalmente, mas a consolidação automática depende de créditos disponíveis."
                    closeLabel="Fechar"
                />
            ),
            size: 'xl',
        });
    };

    const handleRetryConsolidation = () => {
        if (correctWithAiAccess.shouldBlock) {
            toast.warning(getEntitlementBlockedMessage(correctWithAiAccess.decision));
            return;
        }

        retryMutation.mutate();
    };

    const canRetry =
        (test.consolidation_status === 'failed_usage_limit' ||
            test.consolidation_status === 'failed_system') &&
        test.consolidation_error_code !== 'consolidation_unsupported';
    const shouldShowPlans = test.consolidation_status === 'failed_usage_limit';

    return (
        <DataTableRow>
            <DataTableCell>
                <span className="font-medium text-foreground">{test.test_name}</span>
            </DataTableCell>
            <DataTableCell>
                <div className="flex flex-col items-start gap-1.5">
                    <TestStatusBadge status={test.status} assignmentState={test.assignment_state} />
                    <TestConsolidationBadge
                        status={test.consolidation_status}
                        errorMessage={test.consolidation_error_message}
                    />
                </div>
            </DataTableCell>
            <DataTableCell variant="subtle">{formatDateTime(test.created_at)}</DataTableCell>
            <DataTableCell variant="subtle">{formatDateTime(test.answered_at)}</DataTableCell>
            <DataTableCell>
                {isCompletedAssignment ? (
                    <div className="flex items-center gap-1.5">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                                onViewAnswers({
                                    assignmentId: test.id,
                                    testName: test.test_name,
                                })
                            }
                        >
                            <Eye />
                            Visualizar respostas
                        </Button>

                        {(canRetry || shouldShowPlans) && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="iconSm" aria-label="Mais ações">
                                        <MoreVertical className="size-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                    {canRetry && (
                                        <DropdownMenuItem
                                            onClick={handleRetryConsolidation}
                                            disabled={retryMutation.isPending}
                                            className="cursor-pointer"
                                        >
                                            <ArrowRotateLeft
                                                className={cn(
                                                    'mr-2 size-4',
                                                    retryMutation.isPending && 'animate-spin',
                                                )}
                                            />
                                            {retryMutation.isPending
                                                ? 'Tentando novamente'
                                                : 'Tentar novamente'}
                                        </DropdownMenuItem>
                                    )}
                                    {shouldShowPlans && (
                                        <DropdownMenuItem
                                            onClick={handleOpenPlansModal}
                                            className="cursor-pointer"
                                        >
                                            <Card className="mr-2 size-4" />
                                            Ver planos
                                        </DropdownMenuItem>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}
                    </div>
                ) : isOverdue ? (
                    <span className="text-sm text-muted-foreground">Link expirado</span>
                ) : (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:bg-brand-subtle hover:text-brand"
                            >
                                <Share />
                                Compartilhar
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem
                                onClick={() => handleShare('copy')}
                                className="cursor-pointer"
                            >
                                <Copy className="mr-2 size-4" />
                                Copiar link
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => handleShare('whatsapp')}
                                className="cursor-pointer"
                            >
                                <WhatsAppIcon className="mr-2 size-4" />
                                Compartilhar no WhatsApp
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </DataTableCell>
        </DataTableRow>
    );
}
